import { selfBookLinkByToken, selfBookLinkKey, useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { isStoreFallbackEnabled } from '@/api/apiMode';
import { authHeaders } from '@/api/persist';
import { withServerAuthoredWrites } from '@/state/persistenceBridge';
import { flushWritesAndWait } from '@/state/writeQueue';
import { eventBus } from '@/events/eventBus';
import { sweepExpiredLinks } from '@/domain/depositFlow';
import { reconcileSequenceClock } from '@/domain/sequenceFlow';
import { reconcileWaiverDispatch } from '@/domain/waiverFlow';
import { reconcileTopupSchedule } from '@/domain/topupSchedulerFlow';
import { studioDayKey, studioLocalIso } from '@atink/domain';
import type { DepositLanding, PayoutRecord, SettlementLump, Waiver } from '@/domain/types';

/**
 * ONE "day advanced" hook (tv-2na.10.13, D3).
 *
 * Every FORWARD move of the facilitator's clock — the header stepper (stepBy /
 * jumpToDate), the console Age-the-cohort (advanceSequenceClock) and the console
 * deposit-sweep — runs the same bundle through here:
 *   - the deposit-link expiry sweep (tv-2na.10.11),
 *   - the top-up schedule (scheduled → reminded → missed; Ops Overdue top-ups and
 *     the Agent chase read `missed`),
 *   - the T-4d waiver dispatch,
 *   - the client-only sequence clock (brain sequenceState).
 *
 * Served build: POST /api/intents/day-advanced runs the first three on the SERVER
 * (packages/db runDayAdvanced, the same bundle the worker's daily pass runs) on
 * the sim `nowIso`, and this mirrors exactly what the server changed into the
 * store, marked server-authored so nothing is re-PUT. The browser reconcilers
 * never run there (they would race the server with client-authored writes).
 * Store-fallback build: the in-browser reconcilers, as before.
 *
 * Never throws; backward moves never run it.
 */

interface ServerExpired {
  depositId: string;
  releasedBookingId?: string;
  activityId?: string;
}
interface ServerTopup {
  planId: string;
  depositId: string;
  customerId: string;
  dueIso: string;
  to: 'reminded' | 'missed';
}
interface ServerWaiver {
  waiverId: string;
  bookingId: string;
  customerId: string;
  studioId: string;
  artistId?: string;
  sentAt: string;
}

/** B1: the rows the server's open-day rollover wrote, shaped like bootstrap. */
interface ServerRolloverRecords {
  settlementLumps?: Record<string, SettlementLump>;
  payoutRecords?: Record<string, PayoutRecord>;
  depositLandings?: Record<string, DepositLanding>;
}

export type DayAdvancedOutcome =
  | { ok: true; expired: string[]; topups: number; waivers: number }
  | { ok: false; error: string };

const nowIso = (): string => useSimulationStore.getState().clockIso;

export function runDayAdvanced(): Promise<DayAdvancedOutcome> {
  reconcileSequenceClock();
  if (isStoreFallbackEnabled()) return runInBrowser();
  const at = nowIso();
  return flushWritesAndWait()
    .then(() =>
      fetch('/api/intents/day-advanced', {
        method: 'POST',
        headers: { 'content-type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ nowIso: at }),
      }),
    )
    .then(async (res): Promise<DayAdvancedOutcome> => {
      if (!res.ok) return { ok: false, error: `http-${res.status}` };
      const body = (await res.json()) as {
        expired?: ServerExpired[];
        topups?: ServerTopup[];
        waivers?: ServerWaiver[];
        rolloverRecords?: ServerRolloverRecords;
        selfBook?: { expired?: Array<{ token: string }>; pinsCleared?: string[] };
      };
      const expired = body.expired ?? [];
      const topups = body.topups ?? [];
      const waivers = body.waivers ?? [];
      mirrorExpired(expired, at);
      mirrorTopups(topups, at);
      mirrorWaivers(waivers);
      mirrorRollover(body.rolloverRecords);
      mirrorSelfBook(body.selfBook, at);
      return { ok: true, expired: expired.map((e) => e.depositId), topups: topups.length, waivers: waivers.length };
    })
    .catch((err): DayAdvancedOutcome => {
      console.error('[day-advanced] failed', err);
      return { ok: false, error: 'network' };
    });
}

/** Store-fallback: every reconciler runs SYNCHRONOUSLY (the fallback sweep is sync). */
function runInBrowser(): Promise<DayAdvancedOutcome> {
  const swept = sweepExpiredLinks();
  reconcileTopupSchedule();
  reconcileWaiverDispatch();
  return swept.then((r) => (r.ok ? { ok: true, expired: r.expired, topups: 0, waivers: 0 } : r));
}

/**
 * Move the sim clock FORWARD by whole `days` (offset preserving, the header
 * stepper's stepDay) or by `hours` (Age-the-cohort), then run the bundle. A
 * zero / negative step is refused: nothing moves and nothing runs.
 */
export function advanceSimClock(step: { days?: number; hours?: number }): Promise<DayAdvancedOutcome | undefined> {
  const sim = useSimulationStore.getState();
  if (step.days !== undefined) {
    if (!(step.days > 0)) return Promise.resolve(undefined);
    sim.stepDay(step.days);
  } else {
    const hours = step.hours ?? 0;
    if (!(hours > 0)) return Promise.resolve(undefined);
    // The canonical studio-local form (real +10:00/+11:00 offset), as stepDay emits.
    const nextMs = new Date(sim.clockIso).getTime() + hours * 3_600_000;
    sim.setClock(studioLocalIso(nextMs));
  }
  return runDayAdvanced();
}

// ── Mirrors (server-authored: the server already persisted these rows) ─────────

function mirrorExpired(swept: readonly ServerExpired[], at: string): void {
  // Same mirror as depositFlow's sweepExpiredLinks (not exported there; W5b
  // integration may fold the two once Track B's depositFlow lands).
  if (swept.length === 0) return;
  const pairs: Array<readonly [string, string]> = [];
  for (const e of swept) {
    pairs.push(['deposits', e.depositId]);
    if (e.releasedBookingId) pairs.push(['bookings', e.releasedBookingId]);
  }
  withServerAuthoredWrites(pairs, () => {
    const brain = useBrainStore.getState();
    for (const e of swept) {
      const deposit = brain.deposits[e.depositId];
      if (!deposit) continue;
      brain.upsertDeposit({ ...deposit, status: 'expired' });
      const held = e.releasedBookingId ? brain.bookings[e.releasedBookingId] : undefined;
      if (held && held.status === 'held') brain.upsertBooking({ ...held, status: 'cancelled' });
      brain.appendActivity({
        id: e.activityId ?? `act-dep-expired-${deposit.id}`,
        type: 'deposit-expired',
        customerId: deposit.customerId,
        studioId: deposit.studioId,
        artistId: deposit.artistId,
        amount: deposit.amountAud,
        body: `Deposit link expired for ${deposit.customerId}. ${held ? 'Held slot released; ' : ''}record kept.`,
        timestamp: at,
      });
    }
  });
}

function mirrorTopups(transitions: readonly ServerTopup[], at: string): void {
  for (const t of transitions) {
    const brain = useBrainStore.getState();
    const plan = brain.topupPlans[t.planId];
    if (!plan) continue;
    // Match the instalment by its studio due DAY (the server echoes UTC `Z`, the
    // store may hold the +10:00 form of the same instant).
    const dueDay = studioDayKey(t.dueIso);
    const idx = plan.instalments.findIndex((i) => studioDayKey(i.dueIso) === dueDay && i.status !== 'paid');
    if (idx === -1) continue;
    const instalments = plan.instalments.map((inst, i) =>
      i === idx ? { ...inst, status: t.to, ...(t.to === 'reminded' ? { remindedAt: at } : {}) } : inst,
    );
    withServerAuthoredWrites(
      [
        ['topupPlans', t.planId],
        ['deposits', t.depositId],
      ],
      () => {
        useBrainStore.getState().upsertTopupPlan({ ...plan, instalments });
        // The server commits the reminder message with the transition. Its
        // customers event refreshes the conversation; never send again here.
        const deposit = useBrainStore.getState().deposits[t.depositId];
        if (t.to === 'reminded' && deposit) {
          useBrainStore.getState().upsertDeposit({ ...deposit, lastTopupReminderAt: at });
        }
      },
    );
    if (t.to === 'missed') {
      eventBus.publish('topup:missed', {
        planId: t.planId,
        depositId: t.depositId,
        customerId: t.customerId,
        dueIso: t.dueIso,
        at,
      });
    }
  }
}

function mirrorWaivers(sent: readonly ServerWaiver[]): void {
  for (const w of sent) {
    withServerAuthoredWrites([['waivers', w.waiverId], ['customers', w.customerId]], () => {
      // B6 (D06-23): the server wrote the waiver, linked it on the customer record
      // AND texted it (a server row that reaches open records through the customers
      // doorbell), so this tab only mirrors the waiver — no second conversation
      // line, and no PUT of the customer (one per texted customer swamped a step).
      const waiver: Waiver = {
        id: w.waiverId,
        customerId: w.customerId,
        bookingId: w.bookingId,
        status: 'sent',
        sendChannel: 'sms',
        sentAt: w.sentAt,
        createdAt: w.sentAt,
      };
      useBrainStore.getState().upsertWaiver(waiver);
    });
  }
}

/**
 * B1: past, unclosed days the server rolled forward — the bank batch of their card
 * dockets, unsigned envelopes written as deferrals, live deposit money landed.
 * Payouts, the Day sheet and Reconciliation read the store, so mirror the rows now.
 */
function mirrorRollover(records: ServerRolloverRecords | undefined): void {
  if (!records) return;
  const lumps = Object.values(records.settlementLumps ?? {});
  // A day this browser already paid (signed) or handed over (sent) stays as it is:
  // the rollover read the day before that write reached the server.
  const local = useBrainStore.getState().payoutRecords;
  const payouts = Object.values(records.payoutRecords ?? {}).filter(
    (p) => local[p.id]?.status !== 'signed' && local[p.id]?.status !== 'sent',
  );
  const landings = records.depositLandings ?? {};
  const pairs: Array<readonly [string, string]> = [
    ...lumps.map((l) => ['settlementLumps', l.id] as const),
    ...payouts.map((p) => ['payoutRecords', p.id] as const),
    ...Object.keys(landings).map((id) => ['depositLandings', id] as const),
  ];
  if (pairs.length === 0) return;
  withServerAuthoredWrites(pairs, () => {
    const brain = useBrainStore.getState();
    for (const l of lumps) brain.upsertSettlementLump(l);
    for (const p of payouts) brain.upsertPayoutRecord(p);
    useBrainStore.setState((st) => ({ depositLandings: { ...st.depositLandings, ...landings } }));
  });
}

/**
 * B5: the server's self-book sweep, mirrored (server-authored, no re-PUT): named
 * links it expired, and universal links whose lapsed pin it cleared.
 */
function mirrorSelfBook(res: { expired?: Array<{ token: string }>; pinsCleared?: string[] } | undefined, at: string): void {
  const brain = useBrainStore.getState();
  const links = brain.selfBookLinks;
  const expired = (res?.expired ?? []).filter((e) => selfBookLinkByToken(links, e.token));
  const cleared = (res?.pinsCleared ?? []).filter((t) => selfBookLinkByToken(links, t));
  if (expired.length === 0 && cleared.length === 0) return;
  withServerAuthoredWrites(
    [
      ...expired.map((e) => ['selfBookLinks', selfBookLinkKey(links, e.token)] as const),
      ...cleared.map((t) => ['selfBookLinks', selfBookLinkKey(links, t)] as const),
    ],
    () => {
      for (const e of expired) {
        const l = selfBookLinkByToken(useBrainStore.getState().selfBookLinks, e.token);
        if (l) useBrainStore.getState().upsertSelfBookLink({ ...l, status: 'expired', expiredAt: at });
      }
      for (const t of cleared) {
        const l = selfBookLinkByToken(useBrainStore.getState().selfBookLinks, t);
        if (l) {
          const { pinnedStartIso: _p, pinnedDepositId: _d, ...rest } = l;
          useBrainStore.getState().upsertSelfBookLink({ ...rest, sessionLength: undefined, depositAud: undefined });
        }
      }
    },
  );
}
